/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Staged-attachment tray — Design-system `4058:6355/6356/6361/6381`
// (Attachment Tray — Desktop States) + tiles `4058:6353/6354`. Shown
// above the composer when the user has staged images/files (via the
// "+" Add images/Add files menu, paste, camera, or drag-drop). Each
// tile is removable; the whole set sends together on submit. Hidden
// when empty; surfaces a "max reached" hint at 10 items.

import { Image, Pressable, ScrollView, Text, View } from "react-native";
import { Play, X } from "lucide-react-native";

export const MAX_STAGED_ATTACHMENTS = 10;

export interface StagedAttachment {
	readonly id: string;
	readonly kind: "image" | "video" | "file";
	/** Object URL / local uri for the image OR video-poster thumbnail. */
	readonly previewUri?: string | undefined;
	/** File name (file kind). */
	readonly name?: string | undefined;
	/** Human-readable size, e.g. "2.1 MB" (file kind). */
	readonly sizeLabel?: string | undefined;
	/** Duration label for the video kind, e.g. "0:13". */
	readonly durationLabel?: string | undefined;
	/**
	 * Optional caption applied to this attachment via the
	 * `AttachmentPreviewOverlay`. When set, a small caption line renders
	 * on the tile and the host sends it alongside the attachment.
	 */
	readonly caption?: string | undefined;
}

export interface AttachmentTrayProps {
	readonly attachments: readonly StagedAttachment[];
	readonly onRemove: (id: string) => void;
	/**
	 * Optional — when supplied, tapping a tile opens the host's
	 * attachment preview + caption overlay for that attachment id
	 * (via `<AttachmentPreviewOverlay>`).
	 */
	readonly onPressStaged?: ((id: string) => void) | undefined;
}

export function AttachmentTray({
	attachments,
	onRemove,
	onPressStaged,
}: AttachmentTrayProps): JSX.Element | null {
	if (attachments.length === 0) return null;
	const atMax = attachments.length >= MAX_STAGED_ATTACHMENTS;
	return (
		<View className="px-lg pt-sm">
			<ScrollView
				horizontal
				showsHorizontalScrollIndicator={false}
				contentContainerClassName="flex flex-row gap-sm py-1"
			>
				{attachments.map((att) => (
					<View key={att.id} className="relative">
						<Pressable
							disabled={onPressStaged === undefined}
							onPress={() => onPressStaged?.(att.id)}
							accessibilityRole={
								onPressStaged !== undefined ? "button" : undefined
							}
							accessibilityLabel={
								onPressStaged !== undefined
									? `Edit ${att.name ?? "attachment"}`
									: undefined
							}
						>
							{att.kind === "image" ? (
								<View className="w-20 h-20 rounded-[12px] overflow-hidden bg-light-surface-secondary dark:bg-[#222429]">
									{att.previewUri !== undefined ? (
										<Image
											source={{ uri: att.previewUri }}
											accessibilityIgnoresInvertColors
											resizeMode="cover"
											// NativeWind `w-full h-full` on a react-native-web
											// <Image> collapses to 0×0 (css-interop sizing on the
											// RN Image node is unreliable across the workspace's
											// dual RN instances); explicit numeric style is the
											// documented class-can't-express fallback. Matches the
											// working Avatar / ImageAttachmentCard pattern.
											style={{ width: "100%", height: "100%" }}
										/>
									) : null}
								</View>
							) : att.kind === "video" ? (
								<View className="w-20 h-20 rounded-[12px] overflow-hidden bg-light-surface-secondary dark:bg-[#222429] items-center justify-center">
									{att.previewUri !== undefined ? (
										<Image
											source={{ uri: att.previewUri }}
											accessibilityIgnoresInvertColors
											resizeMode="cover"
											style={{ width: "100%", height: "100%" }}
										/>
									) : null}
									{/* Center play disc overlay */}
									<View className="absolute inset-0 items-center justify-center">
										<View className="w-7 h-7 rounded-full items-center justify-center bg-[#E8EAED]">
											<Play size={14} color="#4B515C" fill="#4B515C" />
										</View>
									</View>
									{att.durationLabel !== undefined ? (
										<View className="absolute bottom-1 left-1 rounded-full bg-black/55 px-1.5 py-0.5">
											<Text className="font-sans text-[9px] leading-3 text-white">
												{att.durationLabel}
											</Text>
										</View>
									) : null}
								</View>
							) : att.previewUri !== undefined ? (
								// Rendered page thumbnail (PDF first page / text page) with
								// the file-type badge overlaid bottom-left.
								<View className="w-20 h-20 rounded-[12px] overflow-hidden bg-light-surface-secondary dark:bg-[#222429]">
									<Image
										source={{ uri: att.previewUri }}
										accessibilityIgnoresInvertColors
										resizeMode="cover"
										style={{ width: "100%", height: "100%" }}
									/>
									<View className="absolute bottom-1 left-1 rounded-[6px] px-1.5 py-0.5 bg-light-accent-brand dark:bg-light-accent-brand">
										<Text className="font-sans text-[10px] font-bold leading-3 text-white">
											{fileExt(att.name)}
										</Text>
									</View>
								</View>
							) : (
								<View className="w-20 h-20 rounded-[12px] p-2 bg-light-surface-secondary dark:bg-[#222429] justify-end gap-1">
									<View className="self-start rounded-[6px] px-1.5 py-0.5 bg-light-accent-brand dark:bg-light-accent-brand">
										<Text className="font-sans text-[10px] font-bold leading-3 text-white">
											{fileExt(att.name)}
										</Text>
									</View>
									<Text
										numberOfLines={1}
										className="font-sans text-[11px] leading-[14px] text-light-text-primary dark:text-[#F5F6F8]"
									>
										{att.name ?? "File"}
									</Text>
									{att.sizeLabel !== undefined ? (
										<Text className="font-sans text-[10px] leading-3 text-light-text-secondary dark:text-[#B5BAC3]">
											{att.sizeLabel}
										</Text>
									) : null}
								</View>
							)}
							{att.caption !== undefined && att.caption.length > 0 ? (
								<Text
									numberOfLines={1}
									className="font-sans text-[10px] leading-[14px] pt-1 max-w-20 text-light-text-secondary dark:text-[#B5BAC3]"
								>
									{att.caption}
								</Text>
							) : null}
						</Pressable>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel={`Remove ${att.name ?? "attachment"}`}
							onPress={() => onRemove(att.id)}
							hitSlop={6}
							className="absolute top-1 right-1 w-5 h-5 rounded-full items-center justify-center bg-black/60"
						>
							<X size={12} strokeWidth={2.5} color="#FFFFFF" />
						</Pressable>
					</View>
				))}
			</ScrollView>
			{atMax ? (
				<Text className="font-sans text-[11px] leading-[14px] text-light-accent-rose pt-1">
					You can attach up to {MAX_STAGED_ATTACHMENTS} files.
				</Text>
			) : null}
		</View>
	);
}

function fileExt(name: string | undefined): string {
	if (name === undefined) return "FILE";
	const dot = name.lastIndexOf(".");
	if (dot === -1 || dot === name.length - 1) return "FILE";
	return name.slice(dot + 1).toUpperCase().slice(0, 4);
}
